'use client'

import { useCallback, useEffect, useMemo } from 'react'
import debounce from 'lodash.debounce'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import qs from 'qs'

import { useFilterChange } from 'modules/products'
import { RadioItem } from 'shared/ui/RadioGroup'

import {
  createQueryString,
  FILTER_NAMES,
  mapPriceFiltersToQuery,
  PRICE_FILTER_OPTIONS,
  PriceFilterValidator,
  PriceType
} from '../utils'

import { ClearButton } from './ClearButton'
import HorizontalExpendablePanel from './HorizontalExpendablePanel'
import PriceMinMaxInput from './PriceMinMaxInput'
import { FiltersRadioGroup } from './styles'

interface PriceFilterProps {
  isActive: boolean
}
const PriceFilter = ({ isActive }: PriceFilterProps) => {
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const { setLastFilterChange } = useFilterChange()

  const prices = useMemo(() => {
    if (!searchParams) return { min: '', max: '' }
    const parsed = qs.parse(searchParams.toString())
    const priceString = (parsed.filter as { price?: string })?.price || ''
    const [min, max] = priceString.split('|')

    return {
      min: min ? (parseInt(min, 10) / 100).toString() : '',
      max: max ? (parseInt(max, 10) / 100).toString() : ''
    }
  }, [searchParams])

  const updateQuery = useCallback(
    (min: string, max: string) => {
      const priceQueryValue = mapPriceFiltersToQuery({ min, max })
      const newQueryString = createQueryString(
        new URLSearchParams(searchParams || ''),
        'price',
        priceQueryValue
      )

      setLastFilterChange(new Date())
      router.replace(`${pathname}?${newQueryString}`)
    },
    [searchParams, pathname, router, setLastFilterChange]
  )

  const handlePriceSetSelect = useCallback(
    (range: string) => {
      const [min, max] = range.split('|')

      updateQuery(min, max)
    },
    [updateQuery]
  )

  const handlePriceChange = useCallback(
    ({ min, max }: PriceType) => {
      updateQuery(min, max)
    },
    [updateQuery]
  )

  const selectedPrice = useMemo(() => {
    if (PriceFilterValidator.isNoSelected(prices)) return '|'
    if (PriceFilterValidator.isUnder10(prices)) return '|10'
    if (PriceFilterValidator.isBetween10_20(prices)) return '10|20'
    if (PriceFilterValidator.isBetween20_50(prices)) return '20|50'
    if (PriceFilterValidator.isAbove50(prices)) return '50|'

    return null
  }, [prices])

  const handleRadioChange = useMemo(
    () =>
      debounce((radioValue: string) => {
        handlePriceSetSelect(radioValue)
      }, 300),
    [handlePriceSetSelect]
  )

  useEffect(() => {
    return () => {
      handleRadioChange.cancel()
    }
  }, [handleRadioChange])

  return (
    <HorizontalExpendablePanel title={FILTER_NAMES.Price} isActive={isActive}>
      {isActive && (
        <ClearButton onClick={() => handlePriceSetSelect('|')}>
          Wyczyść filtr
        </ClearButton>
      )}

      <FiltersRadioGroup
        name='price-filter'
        selectedValue={selectedPrice}
        onChange={handleRadioChange}
      >
        {PRICE_FILTER_OPTIONS.map(option => (
          <RadioItem key={option.label} value={option.min + '|' + option.max}>
            {option.label}
          </RadioItem>
        ))}
      </FiltersRadioGroup>
      <PriceMinMaxInput
        debounce={1000}
        onChange={handlePriceChange}
        minValue={prices.min}
        maxValue={prices.max}
      />
    </HorizontalExpendablePanel>
  )
}

export default PriceFilter
